import { useState, useEffect } from 'react';
import { Socket } from 'socket.io-client';
import toast from 'react-hot-toast';

interface BackdropsPanelProps {
  sessionId: string;
  socket: Socket | null;
  isConnected: boolean;
}

interface BackdropImage {
  name: string;
  path: string;
}

export function BackdropsPanel({ sessionId, socket, isConnected }: BackdropsPanelProps) {
  const [selectedBackdrop, setSelectedBackdrop] = useState<string | null>('black');
  const [images, setImages] = useState<BackdropImage[]>([]);

  useEffect(() => {
    fetch('/api/backdrops')
      .then(res => res.json())
      .then(data => setImages(data))
      .catch(() => setImages([]));
  }, []);

  const handleSelectBlack = () => {
    if (!socket || !isConnected) {
      toast.error('Socket non connesso');
      return;
    }

    setSelectedBackdrop('black');
    socket.emit('admin:set-projection', {
      sessionId,
      mode: { type: 'backdrop', url: 'black' },
    });
    toast.success('Sfondo nero attivato');
  };

  const handleSelectStandby = () => {
    if (!socket || !isConnected) {
      toast.error('Socket non connesso');
      return;
    }

    setSelectedBackdrop('standby');
    socket.emit('admin:set-projection', {
      sessionId,
      mode: { type: 'standby' },
    });
    toast.success('Schermata Dungeon World attivata');
  };

  const handleSelectImage = (image: BackdropImage) => {
    if (!socket || !isConnected) {
      toast.error('Socket non connesso');
      return;
    }

    setSelectedBackdrop(image.path);
    socket.emit('admin:set-projection', {
      sessionId,
      mode: { type: 'backdrop', url: image.path },
    });
    toast.success(`Sfondo "${image.name}" attivato`);
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <h3 className="text-xl font-bold text-white mb-4">Sfondi</h3>

      {/* Pulsanti Nero e Dungeon World */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={handleSelectBlack}
          className={`flex-1 py-4 rounded-lg font-bold text-lg transition-all ${
            selectedBackdrop === 'black'
              ? 'bg-gray-900 text-white ring-4 ring-purple-500 shadow-lg shadow-purple-500/30'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Nero
        </button>
        <button
          onClick={handleSelectStandby}
          className={`flex-1 py-4 rounded-lg font-bold text-lg transition-all ${
            selectedBackdrop === 'standby'
              ? 'bg-indigo-700 text-white ring-4 ring-purple-500 shadow-lg shadow-purple-500/30'
              : 'bg-indigo-900 text-indigo-200 hover:bg-indigo-800'
          }`}
        >
          Dungeon World
        </button>
      </div>

      {/* Griglia miniature con wrap */}
      {images.length > 0 ? (
        <div className="flex flex-wrap gap-4">
          {images.map((image) => {
            const fileName = image.name;

            return (
              <button
                key={image.path}
                onClick={() => handleSelectImage(image)}
                className={`relative w-48 aspect-video rounded-lg overflow-hidden transition-all ${
                  selectedBackdrop === image.path
                    ? 'ring-4 ring-purple-500 shadow-lg shadow-purple-500/30 scale-105'
                    : 'hover:ring-2 hover:ring-gray-500 hover:scale-102'
                }`}
              >
                <img
                  src={image.path}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
                {/* Nome file centrato in sovrapposizione */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <p className="text-white text-sm font-bold text-center px-2 drop-shadow-lg">
                    {fileName}
                  </p>
                </div>
                {selectedBackdrop === image.path && (
                  <div className="absolute top-2 right-2 bg-purple-500 rounded-full p-1">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-400 text-center py-8">
          Nessuna immagine disponibile. Carica immagini in <code className="bg-gray-700 px-2 py-1 rounded">packages/client/public/images/</code>
        </p>
      )}

      {!isConnected && (
        <div className="mt-4 p-3 bg-yellow-900/30 border border-yellow-600 rounded-lg">
          <p className="text-yellow-300 text-sm">
            Socket non connesso. Gli sfondi non possono essere proiettati.
          </p>
        </div>
      )}
    </div>
  );
}
